import { Slider, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import styles from "./time-slider.module.scss";
import { QuickSelect } from "./quick-select";
import { DateTime, Zone } from "luxon";

export interface ITimeSliderProps {
  value: DateTime;
  onChange: (value: DateTime) => void;
}

export function TimeSlider(props: ITimeSliderProps) {
  const STEP_MINUTES = 15;
  const RANGE_HOURS = 4;
  const MARK_INTERVAL_HOURS = 1;

  const now = DateTime.now();
  const fourHoursAgo = now.minus({ hours: RANGE_HOURS });

  const minMillis = fourHoursAgo.toMillis();
  const maxMillis = now.toMillis();
  const stepMillis = STEP_MINUTES * 60 * 1000;

  const marks = useMemo(() => {
    const marksArr: { value: number; label: string }[] = [];

    let cursor = fourHoursAgo.startOf("hour");

    while (cursor <= now) {
      marksArr.push({
        value: cursor.toMillis(),
        label: cursor.toFormat("hh a"), // e.g. 03 PM
      });

      cursor = cursor.plus({ hours: MARK_INTERVAL_HOURS });
    }

    return marksArr;
  }, [fourHoursAgo, now]);

  const getTooltipValue = (val: number | number[]) => {
    const selectedMillis = Array.isArray(val) ? val[0] : val;

    return DateTime.fromMillis(selectedMillis).toFormat("ccc d hh:mm a");
  };

  const handleTimeChange = (value: DateTime | number) => {
    let formattedVal: DateTime;
    if (!(value instanceof DateTime)) {
      formattedVal = DateTime.fromMillis(value);
    } else {
      formattedVal = value;
    }
    if (formattedVal.equals(props.value)) {
      return;
    }

    props.onChange(formattedVal);
  };

  return (
    <div className={styles.container}>
      <Typography gutterBottom>Restore Point</Typography>
      <Slider
        sx={{
          height: 8,
        }}
        value={props.value.toMillis()}
        min={minMillis}
        max={maxMillis}
        step={stepMillis}
        marks={marks}
        getAriaValueText={getTooltipValue}
        valueLabelFormat={getTooltipValue}
        valueLabelDisplay="on"
        track="inverted"
        onChange={(event, value) => {
          const val = Array.isArray(value) ? value[0] : value;
          handleTimeChange(val);
        }}
      />
      <QuickSelect
        className={styles["quick-select"]}
        initial={now}
        value={props.value}
        onChange={handleTimeChange}
      />
    </div>
  );
}
